/**
 * TimerDisplay - Quiz Timer Component
 * 
 * Displays countdown timer with visual urgency indicators.
 * Supports bonus time display and color-coded urgency levels.
 * 
 * Features:
 * - Circular progress indicator
 * - Color transitions (green → yellow → orange → red)
 * - Bonus time display
 * - Pulse animation when urgent
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { TIMER_URGENCY } from '../hooks/useQuizTimer';

/**
 * TimerDisplay component
 * @param {number} timeRemaining - Seconds remaining
 * @param {boolean} timerExpired - Whether timer has expired
 * @param {boolean} timerActive - Whether timer is running
 * @param {string} urgencyLevel - Current urgency level (TIMER_URGENCY)
 * @param {string} colorClass - Tailwind color class for timer
 * @param {number} bonusTime - Bonus seconds (from time extension joker)
 * @param {boolean} showLabel - Whether to show "Zeit" label
 * @param {string} size - Size variant: 'sm' | 'md' | 'lg'
 */
export function TimerDisplay({
  timeRemaining,
  timerExpired = false,
  timerActive = true,
  urgencyLevel = TIMER_URGENCY.NORMAL,
  colorClass = 'text-green-500',
  bonusTime = 0,
  showLabel = true,
  size = 'md',
}) {
  const { darkMode } = useTheme();

  // Size variants
  const sizeClasses = {
    sm: {
      container: 'w-12 h-12',
      text: 'text-lg',
      label: 'text-xs',
      bonus: 'text-xs',
    },
    md: {
      container: 'w-16 h-16',
      text: 'text-2xl',
      label: 'text-xs',
      bonus: 'text-xs',
    },
    lg: {
      container: 'w-20 h-20',
      text: 'text-3xl',
      label: 'text-sm',
      bonus: 'text-sm',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  // Urgency-based styling
  const getUrgencyClasses = () => {
    switch (urgencyLevel) {
      case TIMER_URGENCY.CRITICAL:
        return 'animate-pulse ring-2 ring-red-500 ring-opacity-50';
      case TIMER_URGENCY.URGENT:
        return 'animate-pulse';
      default:
        return '';
    }
  };

  // Background color based on urgency
  const getBgClass = () => {
    if (darkMode) {
      switch (urgencyLevel) {
        case TIMER_URGENCY.CRITICAL:
          return 'bg-red-900/30';
        case TIMER_URGENCY.URGENT:
          return 'bg-orange-900/30';
        case TIMER_URGENCY.WARNING:
          return 'bg-yellow-900/30';
        default:
          return 'bg-gray-800';
      }
    } else {
      switch (urgencyLevel) {
        case TIMER_URGENCY.CRITICAL:
          return 'bg-red-100';
        case TIMER_URGENCY.URGENT:
          return 'bg-orange-100';
        case TIMER_URGENCY.WARNING:
          return 'bg-yellow-100';
        default:
          return 'bg-white';
      }
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    if (seconds <= 0) return '0';
    if (seconds < 60) return Math.ceil(seconds).toString();
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!timerActive && !timerExpired) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-1">
      {/* Timer Circle */}
      <div
        className={`
          ${sizes.container}
          ${getBgClass()}
          ${getUrgencyClasses()}
          rounded-full
          flex items-center justify-center
          shadow-md
          transition-all duration-300
          ${darkMode ? 'border border-gray-700' : 'border border-gray-200'}
        `}
      >
        <span className={`${sizes.text} font-bold ${colorClass}`}>
          {timerExpired ? '0' : formatTime(timeRemaining)}
        </span>
      </div>

      {/* Label */}
      {showLabel && (
        <span className={`${sizes.label} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {timerExpired ? 'Zeit abgelaufen' : 'Sekunden'}
        </span>
      )}

      {/* Bonus Time Indicator */}
      {bonusTime > 0 && !timerExpired && (
        <span className={`${sizes.bonus} text-green-500 font-medium`}>
          +{bonusTime}s Bonus
        </span>
      )}
    </div>
  );
}

/**
 * Compact inline timer for header use
 */
export function InlineTimer({
  timeRemaining,
  timerExpired,
  colorClass = 'text-green-500',
  urgencyLevel = TIMER_URGENCY.NORMAL,
}) {
  const { darkMode } = useTheme();

  const formatTime = (seconds) => {
    if (seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2 py-1
        ${urgencyLevel === TIMER_URGENCY.CRITICAL ? 'animate-pulse' : ''}
      `}
    >
      {/* Clock Icon */}
      <svg
        className={`w-4 h-4 ${colorClass}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>

      <span className={`font-mono font-bold ${colorClass}`}>
        {timerExpired ? 'Zeit!' : formatTime(timeRemaining)}
      </span>
    </div>
  );
}

/**
 * Progress bar style timer
 */
export function TimerBar({
  timeRemaining,
  totalTime,
  timerExpired,
  colorClass = 'bg-green-500',
  urgencyLevel = TIMER_URGENCY.NORMAL,
}) {
  const { darkMode } = useTheme();
  
  const progress = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 0;

  // Map text color to background color
  const getBgColorClass = () => {
    if (colorClass.includes('red')) return 'bg-red-500';
    if (colorClass.includes('orange')) return 'bg-orange-500';
    if (colorClass.includes('yellow')) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="w-full">
      <div
        className={`
          h-2 rounded-full overflow-hidden
          ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
        `}
      >
        <div
          className={`
            h-full rounded-full transition-all duration-1000 ease-linear
            ${getBgColorClass()}
            ${urgencyLevel === TIMER_URGENCY.CRITICAL ? 'animate-pulse' : ''}
          `}
          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        />
      </div>
      
      {timerExpired && (
        <p className={`text-xs text-center mt-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
          Zeit abgelaufen!
        </p>
      )}
    </div>
  );
}

export default TimerDisplay;
